import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { PreviewBox, Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { PlainInput, SwitchLine } from "pages/PayRoll/Shared/statutoryUi";
import { designColors, designType } from "services/constants/designTokens";
import { EXEMPTION_MODE_OPTIONS, emptyExemption, exemptionErrors } from "./ptConfigData";

// Edit Exemption drawer (PT1d). Exemptions are part of the version (no API
// of their own): Save puts the reason into the form, and the form is saved
// with the draft / version calls. An automatic age reason is applied from
// the date of birth every month; the other reasons are offered to HR on PT
// Employees (Mark exempt). "Proof needed" shows only when attachments are
// switched on (attachments_enabled from GET config/options/; off in V1).

export const PtExemptionPanel = ({ open, exemption, form, siteName, readOnly, showProof, modeOptions = EXEMPTION_MODE_OPTIONS, onClose, onSave }) => {
  const adding = !exemption;
  const [draft, setDraft] = useState(() => exemption || emptyExemption());
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(exemption ? { ...exemption } : emptyExemption());
    setShowErrors(false);
  }, [open, exemption]);

  const set = (key) => (value) => setDraft((current) => ({ ...current, [key]: value }));
  const errors = exemptionErrors(draft, form.exemptions);
  const error = (key) => (showErrors ? errors[key] : undefined);
  const auto = draft.mode === "AUTO_AGE";

  const save = () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    onSave({
      ...draft,
      reason: draft.reason.trim().replace(/\s+/g, " "),
      age_from: auto ? String(Number(draft.age_from)) : "",
      rule_text: auto ? "" : String(draft.rule_text || "").trim(),
      proof_required: !auto && showProof ? draft.proof_required : false,
    });
  };

  const effect = auto
    ? `PT is ₹0 from the month the employee is already ${draft.age_from || "this age"} on the 1st. Shown on PT Employees as “Exempt — ${draft.reason || "this reason"}”. Employees at ${siteName} without a date of birth are held, and their payroll is held, until it is entered.`
    : `HR chooses this reason on PT Employees (Mark exempt) for employees of ${siteName}.${showProof && draft.proof_required ? " A proof document must be attached." : ""}`;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={adding ? "Add Exemption" : readOnly ? "Exemption" : "Edit Exemption"}
      subtitle={`${siteName} · ${form.name}`}
      bodySx={panelBodySx}
      footer={
        readOnly ? (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <PrimaryButton onClick={onClose}>Close</PrimaryButton>
          </Mui.Stack>
        ) : (
          <PanelActionFooter onCancel={onClose} primaryLabel={adding ? "Add Exemption" : "Save"} primaryProps={{ onClick: save }} />
        )
      }
    >
      <Mui.Stack spacing={2}>
        {readOnly ? <RequestInfoBanner tone="warning" title="This version is locked." body="A payroll has used it. Create a new version to change exemptions." /> : null}
        <FormSection title="Exemption">
          <Field wide label={<>Exemption reason<Required /></>} helper="Employees already marked with a reason keep it only while the text stays the same." error={error("reason")}>
            <TextField value={draft.reason} onChange={set("reason")} disabled={readOnly} error={error("reason")} placeholder="e.g. Age 65 and above" />
          </Field>
          <Field
            wide
            label={<>How it is applied<Required /></>}
            helper={auto ? "Automatic: Cauver checks every employee each month. One automatic age reason per site." : "HR marks the employee on the PT Employees screen."}
            error={error("mode")}
          >
            <SelectField value={draft.mode} onChange={(value) => value && set("mode")(value)} options={modeOptions} disabled={readOnly} error={error("mode")} />
          </Field>
          {auto ? (
            <Field label={<>Exempt from age<Required /></>} error={error("age_from")}>
              <PlainInput type="number" value={draft.age_from} onChange={set("age_from")} disabled={readOnly} error={error("age_from")} placeholder="e.g. 65" inputProps={{ min: 1, max: 120 }} />
            </Field>
          ) : (
            <>
              <Field wide label="Rule / proof (optional)" helper="Shown in the reasons table, e.g. Certificate, Service ID, 40% or more." error={error("rule_text")}>
                <TextField value={draft.rule_text} onChange={set("rule_text")} disabled={readOnly} error={error("rule_text")} placeholder="e.g. Certificate" />
              </Field>
              {showProof ? (
                <Mui.Box sx={{ gridColumn: "1 / -1" }}>
                  <SwitchLine checked={draft.proof_required} onChange={set("proof_required")} disabled={readOnly} label="Proof needed (HR must attach a document)" />
                </Mui.Box>
              ) : null}
            </>
          )}
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            <PreviewBox>
              <Mui.Typography sx={{ ...designType.tableBodyCell, fontWeight: 600, mb: 0.5 }}>Effect</Mui.Typography>
              <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate700 }}>{effect}</Mui.Typography>
            </PreviewBox>
          </Mui.Box>
        </FormSection>
        <SwitchLine
          checked={draft.status === "Active"}
          onChange={(checked) => set("status")(checked ? "Active" : "Inactive")}
          disabled={readOnly}
          label={`Status: ${draft.status}${draft.status === "Inactive" ? " — no longer offered to HR; employees already marked get a warning in PT Validation" : ""}`}
        />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
